export const DATA_FILTER_OPERATORS = [
    "equals",
    "notEquals",
    "contains",
    "notContains",
    "startsWith",
    "endsWith",
    "gt",
    "lt",
    "gte",
    "lte",
] as const;

export type DataFilterOperator = (typeof DATA_FILTER_OPERATORS)[number];

export const STRING_OPERATORS = ["equals", "notEquals", "contains", "notContains", "startsWith", "endsWith"] as const satisfies readonly DataFilterOperator[];
export const NUMERIC_OPERATORS = ["equals", "notEquals", "gt", "lt", "gte", "lte"] as const satisfies readonly DataFilterOperator[];

export type DataFilterFieldKind = "string" | "number";

export type DataFilterConnector = "AND" | "OR";

/**
 * Controls where a filter is applied across an expandable table's row hierarchy.
 * - "all" (default): applied at both segment-aggregated rows AND nested expansion rows
 * - "segment": applied only at segment-aggregated (top-level) rows; expansion untouched
 * Only meaningful for metric fields on widgets with a segment configured.
 */
export type DataFilterScope = "all" | "segment";

export interface DataFilter {
    field: string;
    operator: DataFilterOperator;
    value: string | number;
    enabled?: boolean;
    connector?: DataFilterConnector;
    scope?: DataFilterScope;
}

/**
 * In-progress shape used by the FE form. Operator and value may be empty
 * while the user is still building the row. Validation gates Create/Preview
 * until every row is complete, after which it round-trips as a `DataFilter`.
 *
 * `error` is populated when the agent flow ships an inferred filter that
 * couldn't be resolved (AC 7) or referenced an unavailable field (AC 8).
 */
export interface DraftDataFilter {
    field: string;
    operator: DataFilterOperator | "";
    value: string | number;
    enabled?: boolean;
    connector?: DataFilterConnector;
    scope?: DataFilterScope;
    error?: string;
}

export interface DataFilterFieldOption {
    /** Actual data key used at filter-eval time (e.g. "campaignName", "segment", "clicks") */
    field: string;
    /** Human label shown in the picker (e.g. "Campaign", "Clicks") */
    label: string;
    kind: DataFilterFieldKind;
}

export const OPERATOR_LABELS: Record<DataFilterOperator, string> = {
    equals: "=",
    notEquals: "≠",
    contains: "Contains",
    notContains: "Does not contain",
    startsWith: "Starts with",
    endsWith: "Ends with",
    gt: ">",
    lt: "<",
    gte: "≥",
    lte: "≤",
};

export function operatorsForKind(kind: DataFilterFieldKind): DataFilterOperator[] {
    return kind === "number" ? [...NUMERIC_OPERATORS] : [...STRING_OPERATORS];
}

export function isDraftFilterComplete(f: DraftDataFilter): f is DataFilter {
    if (!f.field || !f.operator) return false;
    if (f.value === "" || f.value === null || f.value === undefined) return false;
    if (typeof f.value === "number" && Number.isNaN(f.value)) return false;
    return true;
}

const STRICT_NUMERIC_OPS: ReadonlySet<DataFilterOperator> = new Set(["gt", "lt", "gte", "lte"]);
const STRICT_STRING_OPS: ReadonlySet<DataFilterOperator> = new Set(["contains", "notContains", "startsWith", "endsWith"]);

function coerceNumber(input: unknown): number | null {
    if (typeof input === "number") return Number.isFinite(input) ? input : null;
    if (typeof input === "string" && input.trim() !== "") {
        // Strip formatting (currency symbols, thousand separators, percent signs) before parsing.
        const stripped = input.replace(/[^0-9.\-]/g, "");
        if (!stripped) return null;
        const n = parseFloat(stripped);
        return Number.isFinite(n) ? n : null;
    }
    return null;
}

function compareNumeric(a: number, op: DataFilterOperator, b: number): boolean {
    switch (op) {
        case "equals": return a === b;
        case "notEquals": return a !== b;
        case "gt": return a > b;
        case "lt": return a < b;
        case "gte": return a >= b;
        case "lte": return a <= b;
        default: return false;
    }
}

function compareString(a: string, op: DataFilterOperator, b: string): boolean {
    switch (op) {
        case "equals": return a === b;
        case "notEquals": return a !== b;
        case "contains": return a.includes(b);
        case "notContains": return !a.includes(b);
        case "startsWith": return a.startsWith(b);
        case "endsWith": return a.endsWith(b);
        default: return false;
    }
}

/**
 * Evaluates a single data filter against a row. Mirrors the backend's `matchesDataFilter`
 * (ai-dashboard-be/src/utils/dataFilters.ts) so front-end-only filtering — e.g. seeding the
 * campaign selector from a `contains` filter — stays consistent with how the server would filter.
 */
export function matchesDataFilter(row: Record<string, unknown>, filter: DataFilter): boolean {
    // Pass-through when the filter's field isn't a key on this row.
    if (!(filter.field in row)) return true;
    const rowValue = row[filter.field];
    const op = filter.operator;

    if (STRICT_NUMERIC_OPS.has(op)) {
        const a = coerceNumber(rowValue);
        const b = coerceNumber(filter.value);
        if (a === null || b === null) return false;
        return compareNumeric(a, op, b);
    }

    if (STRICT_STRING_OPS.has(op)) {
        return compareString(String(rowValue ?? "").toLowerCase(), op, String(filter.value ?? "").toLowerCase());
    }

    // equals / notEquals — prefer numeric when both values coerce, else string compare.
    const aNum = coerceNumber(rowValue);
    const bNum = coerceNumber(filter.value);
    if (aNum !== null && bNum !== null) {
        return compareNumeric(aNum, op, bNum);
    }
    return compareString(String(rowValue ?? "").toLowerCase(), op, String(filter.value ?? "").toLowerCase());
}
